import { useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import CustomButton from "../CustomButton";
import DataTable, { Column } from "../DataTable";
import ModalLayout from "../layouts/ModalLayout";
import { useDispatch } from "react-redux";
import { Student } from "../../types/studentType";
import { useEffect, useState } from "react";
import { getStudents } from "../../features/student/StudentSlice";
import { useNavigate, useParams } from "react-router-dom";
import { useBranchCode } from "../../util/hooks/useBranch";
import classesApis from "../../api/classesApis";
import StudentApis from "../../api/studentApis";

interface Props {
  onClose?: () => void;
}

export default function AddStudentToClass({ onClose }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const { classId } = useParams<{ classId: string }>();
  const parsedClassId = Number(classId);
  const branchCode = useBranchCode();
  const [modalStudents, setModalStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!branchCode) return;

    setLoading(true);
    StudentApis.getStudents({
      branchCode,
      page: 0,
      size: 1000,
    })
      .then((res) => setModalStudents(res?.data.content ?? []))
      .finally(() => setLoading(false));
  }, [branchCode]);

  const handleSelectStudent = (studentId: number) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleAddStudents = async () => {
    if (!parsedClassId || selectedStudents.length === 0) return;

    try {
      await Promise.all(
        selectedStudents.map((studentId) =>
          classesApis.moveStudentToClass(parsedClassId, studentId),
        ),
      );

      dispatch(
        getStudents({
          page: 0,
          size: 100,
          branchCode,
          classId,
        }),
      );

      onClose?.();
    } catch (error) {
      console.error(error);
    }
  };

  const columns: Column<Student>[] = [
    {
      label: <input type="checkbox" className="w-3 h-3" />,
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          className="w-3 h-3"
          checked={selectedStudents.includes(item.admissionNumber)} // make sure Student has id
          onChange={() => handleSelectStudent(item.admissionNumber)}
        />
      ),
    },

    { label: "SL NO.", accessor: "admissionNumber" },
    {
      label: "Profile",
      accessor: "fullName",
      render: (item) => (
        <div>
          {!item?.photoUrl ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.photoUrl}
              alt=""
              className="flex items-center justify-center object-cover w-10 h-10 rounded-full"
            />
          )}
        </div>
      ),
    },
    {
      label: "Students Name",
      accessor: "fullName",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.fullName}
        </div>
      ),
    },
  ];

  return (
    <ModalLayout onClose={onClose} width="max-w-xl">
      <div className="flex flex-col gap-7 md:max-h-96 overflow-y-auto custom-scrollbar">
        <div className="flex flex-col gap-3">
          <span className="font-semibold text-xl text-neutral-400">
            Add Students to the Class
          </span>
          <div className="flex gap-2 items-center">
            <span className="text-neutral-300 text-sm font-semibold">
              Add from Admission List or{" "}
            </span>
            <button
              onClick={() => navigate("/all-students/add-student")}
              className="text-white rounded text-xs font-medium bg-success-700 px-3 py-1"
            >
              Add New
            </button>
          </div>
        </div>

        <DataTable columns={columns} data={modalStudents} isLoading={loading} />

        <div className="flex gap-4 justify-end items-end">
          <CustomButton
            text="Cancel"
            color="bg-neutral-200"
            onClick={onClose}
          />
          <CustomButton
            text="Add Now"
            color="bg-primary-500"
            onClick={handleAddStudents}
            type="submit"
          />
        </div>
      </div>
    </ModalLayout>
  );
}
